import {
  Box,
  Stack,
  StackProps,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tableCellClasses } from '@mui/material/TableCell';
import _ from 'lodash';
// Components
import { LOGO_INVOICE_URL } from 'src/utils/constants/imagesUrl';
import { useQueryInvoiceDetail } from 'src/services/queries';
import { renderTextOrDefault, renderPriceOrDefault } from 'src/utils/helpers/stringHelpers';
import {FORMAT_DATE_TIME, SIMULATED_LIVE_PLANS} from 'src/utils/constants/config';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import { getInvoiceStatus } from 'src/components/InvoiceTemplate/config';
import { PaymentStatus } from 'src/utils/constants/enum';
import analytics from "src/analytics";
import {useEffect} from "react";
import {useQueryParam} from "src/hooks/useQueryParam";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  [`&.${tableCellClasses.body}`]: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledWhiteBox = styled(Stack)(({ theme }) => ({
  background: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius * 2,
}));

interface IProps extends StackProps {
  isFetchingInvoiceId?: boolean;
  invoiceId: string;
}

const InvoiceTemplate = ({ invoiceId, isFetchingInvoiceId = false, ...others }: IProps) => {
  const { t } = useTranslation();
  const { data, isFetching } = useQueryInvoiceDetail(invoiceId);
  const debug = useQueryParam('_debug');

  useEffect(() => {
    let plan = data?.plan;
    if(debug) {
      plan = SIMULATED_LIVE_PLANS[_.random(0, SIMULATED_LIVE_PLANS.length - 1)] as ExpectedAny
    } else if(!data?.id) return;

    analytics.pushToDataLayer('customer_purchase', {
      purchase_type: plan?.name,
      purchase_value: plan?.amount
    });

  }, [data?.id, data?.plan?.name, data?.plan?.amount, debug]);

  const theme = useTheme();

  const getPlanType = () => {
    const recurringIntervalCount = data?.plan?.recurring_interval_count;
    switch (recurringIntervalCount) {
      case 1:
        return t('pricing.plan_type.monthly');
      case 3:
        return t('pricing.plan_type.quarterly');
      case 6:
        return t('pricing.plan_type.half_yearly');
      case 12:
        return t('pricing.plan_type.yearly');
      default:
        break;
    }
  };

  if (isFetching || isFetchingInvoiceId) {
    return (
      <StyledWhiteBox
        boxShadow={'0 8px 16px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.1)'}
        border={`1px solid ${theme.palette.divider}`}
        width={'100%'}
        {...others}
        p={4}
      >
        <LoadingSkeleton count={6} />
      </StyledWhiteBox>
    );
  }

  return (
    <StyledWhiteBox
      boxShadow={'0 8px 16px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.1)'}
      border={`1px solid ${theme.palette.divider}`}
      width={'100%'}
      {...others}
    >
      <Stack>
        <Box
          display={'flex'}
          flexDirection={'column'}
          gap={1}
          padding={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <Typography variant="h6">{t('invoice_template.invoice')}</Typography>
          <Stack flexDirection={'row'} gap={1}>
            <Typography
              sx={{
                '&::after': {
                  content: '" \\2022"',
                  fontSize: '27px',
                  verticalAlign: 'bottom',
                },
              }}
              color="textTertiary.main"
            >
              {t('invoice_template.billing_id')}{' '}
              <Typography display={'inline'} color={theme.palette.secondary.main}>
                #{data.id}
              </Typography>
            </Typography>
            <Typography
              sx={{
                '&::after': {
                  content: '" \\2022"',
                  fontSize: '27px',
                  verticalAlign: 'bottom',
                },
              }}
              color="textTertiary.main"
            >
              {t('invoice_template.paid_at')}{' '}
              <Typography display={'inline'} color={theme.palette.secondary.main}>
                {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.payment_date)}
              </Typography>
            </Typography>
            <Typography
              sx={{
                '&::after': {
                  content: '" \\2022"',
                  fontSize: '27px',
                  verticalAlign: 'bottom',
                },
              }}
              color="textTertiary.main"
            >
              {t('invoice_template.payment_method')}{' '}
              <Typography display={'inline'} color={theme.palette.secondary.main}>
                {renderTextOrDefault(data?.payment_method)}
              </Typography>
            </Typography>
            <Typography color="textTertiary.main">
              {t('invoice_template.status')} {getInvoiceStatus(data?.status as PaymentStatus)}
            </Typography>
          </Stack>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={2}
          padding={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <div>
            <img alt={'ADAMftd logo'} src={LOGO_INVOICE_URL} />
          </div>
          <Box>
            <Typography variant="subtitle2">ADAMftd Pte Ltd</Typography>
            <Typography color="text.secondary">Bras Basah Complex, 231 Bain St, #03-05, Singapore 180231</Typography>
          </Box>
        </Box>
        <Box
          display={'flex'}
          gap={3}
          flexDirection={'column'}
          padding={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <Typography variant="h6">{t('invoice_template.billed_to')}</Typography>
          <Stack flexWrap={'wrap'} flexDirection={'row'} gap={2}>
            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2}>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'} color="text.secondary">
                  {t('invoice_template.full_name')}
                </Typography>
                <Typography>{renderTextOrDefault(data?.customer_name)}</Typography>
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'} color="text.secondary">
                  {t('invoice_template.email_account')}
                </Typography>
                <Typography>{renderTextOrDefault(data?.customer_email)}</Typography>
              </Box>
              {/* <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'} color="text.secondary">
                  {t('invoice_template.address')}
                </Typography>
                <Typography>2972 Westheimer Rd. Santa Ana, Illinois 85486, USA</Typography>
              </Box> */}
            </Box>
            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2}>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'} color="text.secondary">
                  {t('invoice_template.phone')}
                </Typography>
                <Typography>{renderTextOrDefault(data?.customer_phone)}</Typography>
              </Box>
              {/* <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant={'body2'} color="text.secondary">
                  {t('invoice_template.company')}
                </Typography>
                <Typography>Abstergo Ltd.</Typography>
              </Box> */}
            </Box>
          </Stack>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          gap={3}
          padding={3}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <Typography variant="h6">Items</Typography>
          <TableContainer component={Paper} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t('invoice_template.plan')}</StyledTableCell>
                  <StyledTableCell>{t('invoice_template.type')}</StyledTableCell>
                  {/* <StyledTableCell>{t('invoice_template.price')}</StyledTableCell>
                  <StyledTableCell>{t('invoice_template.quantity')}</StyledTableCell> */}
                  <StyledTableCell>{t('invoice_template.amount')}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell scope="row">{data.plan.name}</StyledTableCell>
                  <StyledTableCell>{getPlanType()}</StyledTableCell>
                  {/* <StyledTableCell>{renderPriceOrDefault(data.plan.price)}</StyledTableCell> */}
                  {/* <StyledTableCell>{data.plan.quantity}</StyledTableCell> */}
                  {/* <StyledTableCell>1</StyledTableCell> */}
                  <StyledTableCell>{renderPriceOrDefault(data.plan.amount)}</StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack width={'40%'} gap={2} alignSelf={'flex-end'}>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Typography variant={'body2'} color="textTertiary.main">
                {t('invoice_template.total')}
              </Typography>
              <Typography>{renderPriceOrDefault(data.plan.amount)}</Typography>
            </Box>
          </Stack>
        </Box>
        <Box padding={3}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            borderRadius={theme.shape.borderRadius / 2}
            bgcolor={theme.palette.secondary.main}
            padding={3}
            justifyContent={'space-between'}
          >
            <Box>
              <Box marginBottom={3} display={'flex'} gap={1} flexDirection={'column'}>
                <Typography fontWeight={'bold'} color={'white'}>
                  {t('invoice_template.invoice_detail')}
                </Typography>
                <Typography color={'white'}>
                  {t('invoice_template.date_issued')}:{' '}
                  {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.payment_date)}
                </Typography>
                <Typography color={'white'}>
                  {t('invoice_template.due_date')}: {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.period_end)}
                </Typography>
              </Box>
              <Box display={'flex'} gap={1} flexDirection={'column'}>
                <Typography fontWeight={'bold'} color={'white'}>
                  {t('invoice_template.invoice_term')}
                </Typography>
                <Typography color={'white'}>
                  {t('invoice_template.automatically_renews_on')}{' '}
                  {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.period_end)}
                </Typography>
              </Box>
            </Box>
            <Box display={'flex'} gap={1} flexDirection={'column'}>
              <Typography align={'right'} fontWeight={'bold'} color={'white'}>
                {t('invoice_template.total_amount')}
              </Typography>
              <Typography variant={'h4'} color={'white'}>
                ${renderPriceOrDefault(data.plan.amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Stack>
    </StyledWhiteBox>
  );
};

export default InvoiceTemplate;
